import { Fragment, useCallback, useState } from "react";
import { Clinic, Doctor, DoctorUser } from "../../models";
import { useNavigate } from "react-router-dom";
import Checkbox from "@mui/material/Checkbox";
import { TablePagination } from "../Common";
import { DOCTOR_TABLE_PAGE_SIZE } from "../../constants";
import { useDoctorContext } from "../../contexts";
import Link from "@mui/material/Link";

interface props {
	filteredDoctors: DoctorUser[];
	clinics: Clinic[];
	clinic?: Clinic;
}

export const DoctorsTable = ({ filteredDoctors, clinics, clinic }: props) => {
	const navigate = useNavigate();
	const { doctorImages } = useDoctorContext();
	const [pagedDoctors, setPagedDoctors] = useState<DoctorUser[]>([]);
	const [expandedDoctorRows, setExpandedDoctorRows] = useState<number[]>([]);

	const toggleExpandedDoctorRow = useCallback((doctorId: number) => {
		setExpandedDoctorRows((currentDoctorIds) =>
			currentDoctorIds.includes(doctorId)
				? currentDoctorIds.filter((id) => id !== doctorId)
				: [...currentDoctorIds, doctorId]
		);
	}, []);

	const toggleDoctorCentre = (key: string) => {
		const parts = key.split("-");
		console.info("toggleDoctorCentre", key, parts);
	};

	const handleDoctorEditClick = useCallback(
		(user: DoctorUser) => {
			navigate("/doctorEdit", { state: { user, clinic } });
		},
		[clinic, navigate]
	);

	const getDoctorImage = useCallback(
		(doctorId: number) => {
			if (!doctorImages || doctorImages.length === 0 || !doctorId) {
				return null;
			}
			const doctorImage = doctorImages.find(
				(doctorImage) => doctorImage.doctorId === doctorId
			);
			return doctorImage?.imageUrl ?? null;
		},
		[doctorImages]
	);

	const tableCellWithImage = useCallback(
		(doctorId: number) => {
			const imageSrc = getDoctorImage(doctorId);

			return (
				<td>
					{imageSrc ? (
						<img src={imageSrc} width="40" height="40" alt="doctor" />
					) : (
						""
					)}
				</td>
			);
		},
		[getDoctorImage]
	);

	const renderClinicsRow = useCallback(
		(doctor: Doctor) => {
			return (
				<tr id={`${doctor.id}-clinics`}>
					<td colSpan={6} className="ch-lightergrey-background">
						<div className="ch-centre-checkbox-container">
							{clinics?.map((clinic: Clinic) => {
								const matchingBPDoctor = doctor.bpDoctors.find(
									(doctor) => doctor.clinicId === clinic.id
								);
								return (
									<div
										key={`${doctor.id}-${clinic.id}`}
										className="ch-centre-checkbox"
									>
										<Checkbox
											value={`${doctor.id}-${clinic.id}`}
											checked={!!matchingBPDoctor}
											disabled={!matchingBPDoctor}
											onChange={() =>
												toggleDoctorCentre(`${doctor.id}-${clinic.id}`)
											}
										/>
										{clinic.name}
									</div>
								);
							})}
						</div>
					</td>
				</tr>
			);
		},
		[clinics]
	);

	const renderDoctorRow = useCallback(
		(user: DoctorUser) => {
			return (
				<Fragment key={user.upn}>
					<tr key={user.upn}>
						{tableCellWithImage(user.doctor.id)}
						<td
							dangerouslySetInnerHTML={{
								__html: `${user.doctor.salutation} ${user.doctor.firstName} ${user.doctor.lastName} <br>${user.upn}`
							}}
						></td>
						<td>{user.doctor.sex !== "None" ? user.doctor.sex : ""}</td>
						<td>
							{user.doctor.status ? (
								<span className="ch-chip ch-chip-green">Active</span>
							) : (
								<span className="ch-chip ch-chip-amber">Inactive</span>
							)}
						</td>
						<td>
							<Link
								underline="none"
								onClick={() => {
									toggleExpandedDoctorRow(user.doctor.id);
								}}
							>
								{expandedDoctorRows.includes(user.doctor.id)
									? "Hide centres"
									: "Show centres"}
							</Link>
						</td>
						<td>
							<Link
								underline="none"
								onClick={() => handleDoctorEditClick(user)}
							>
								Edit
							</Link>
						</td>
					</tr>
					{expandedDoctorRows.includes(user.doctor.id) &&
						renderClinicsRow(user.doctor)}
				</Fragment>
			);
		},
		[
			expandedDoctorRows,
			handleDoctorEditClick,
			renderClinicsRow,
			tableCellWithImage,
			toggleExpandedDoctorRow
		]
	);

	const handlePageChange = (doctors: DoctorUser[]) => {
		setPagedDoctors(doctors);
	};

	return (
		<>
			<table aria-label="doctors" className="ch-table">
				<thead>
					<tr>
						<th></th>
						<th>Name/Entra Id</th>
						<th>Gender</th>
						<th>Status</th>
						<th>Registered Centres</th>
						<th>Edit</th>
					</tr>
				</thead>
				<tbody>{pagedDoctors.map((user) => renderDoctorRow(user))}</tbody>
			</table>
			<TablePagination
				items={filteredDoctors || []}
				pageSize={DOCTOR_TABLE_PAGE_SIZE}
				pageChange={handlePageChange}
			/>
		</>
	);
};
